// admin/src/pages/DocumentExpiryManagementPage.jsx - 문서 만료 관리 대시보드
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Table, Modal, Drawer, Select, Tooltip, Empty, Checkbox, Switch, message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  Clock,
  Users,
  AlertTriangle,
  Send,
  Search,
  CheckCircle,
  XCircle,
  FileText,
  CreditCard,
  IdCard,
  Award,
  Bell,
  RefreshCw,
  Eye,
  Shield,
  Trash2,
  Zap,
  BookOpen,
  MessageSquare,
  X
} from 'lucide-react';
import { db, functions } from '../firebase/firebaseConfig.jsx';
import messageService from '../services/messageService';
import { adminNotificationService } from '../services/adminNotificationService';
import userDeletionService from '../services/userDeletionService';

const APP_ID = import.meta.env.VITE_APP_ID || 'kairos-platform-1ffde';
const STATS_PATH = `artifacts/${APP_ID}/admin/documentExpiryStats`;

const DOC_TYPES = {
  foreigner_registration_card: { mm: 'နိုင်ငံခြားသားမှတ်ပုံတင်ကတ်', Icon: IdCard, criticalDays: 30 },
  visa: { mm: 'ဗီဇာ', Icon: CreditCard, criticalDays: 14 },
  passport: { mm: 'ပတ်စ်ပို့', Icon: BookOpen, criticalDays: 60 },
  certificate: { mm: 'လက်မှတ်', Icon: Award, criticalDays: 30 }
};

const EXPIRING_WINDOW_DAYS = 90;
const DAY_MS = 24 * 60 * 60 * 1000;
const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
const SEND_CONCURRENCY = 20;
const AUTO_ALERT_DEFAULTS = { enabled: true, reminderIntervalDays: 7, expiredLookbackDays: 90 };

// ---------- 날짜 (Asia/Seoul 달력일 기준, functions/src/documentExpiryBatch.js와 동일) ----------
const toKstDayIndex = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    const m = value.trim().match(/^(\d{4})[-./](\d{1,2})[-./](\d{1,2})$/);
    if (m) return Math.floor(Date.UTC(+m[1], +m[2] - 1, +m[3]) / DAY_MS);
  }
  let date;
  if (typeof value?.toDate === 'function') date = value.toDate();
  else if (typeof value === 'object' && typeof value.seconds === 'number') date = new Date(value.seconds * 1000);
  else date = new Date(value);
  const ms = date.getTime();
  if (!Number.isFinite(ms)) return null;
  return Math.floor((ms + KST_OFFSET_MS) / DAY_MS);
};

const kstTodayIndex = () => Math.floor((Date.now() + KST_OFFSET_MS) / DAY_MS);
const kstTodayString = () => new Date(Date.now() + KST_OFFSET_MS).toISOString().slice(0, 10);
const dayIndexToString = (idx) => (idx == null ? '-' : new Date(idx * DAY_MS).toISOString().slice(0, 10));

const toJsDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value === 'object' && typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? d : null;
};

const DATE_LOCALES = { ko: 'ko-KR', en: 'en-US', my: 'my-MM' };

const formatDateTime = (value, lang = 'ko') => {
  const d = toJsDate(value);
  if (!d) return '-';
  return d.toLocaleString(DATE_LOCALES[lang] || 'ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const dDayLabel = (days, tr) => {
  if (days == null) return '-';
  if (days < 0) return tr('docExpiry.dday.expired', { n: Math.abs(days) });
  if (days === 0) return 'D-Day';
  return `D-${days}`;
};

// ---------- 만료 상태 (캐시의 원본 문서로 현재 시점 기준 재계산) ----------
const computeExpiryStatus = (documents = {}, alarms = []) => {
  const today = kstTodayIndex();
  const todayStr = kstTodayString();
  const status = {
    hasExpiringDocuments: false,
    hasExpiredDocuments: false,
    criticalDocuments: [],
    expiringDocuments: [],
    expiredDocuments: [],
    allDocuments: [],
    nearestExpiry: null,
    daysUntilExpiry: null,
    hasTodayAlarm: false,
    todayAlarmDocuments: []
  };

  for (const [docType, docs] of Object.entries(documents || {})) {
    for (const d of docs || []) {
      const raw = d.expiryDate || d.passportExpiry || d.visaExpiryDate || d.expiry;
      const expiryDay = toKstDayIndex(raw);
      if (expiryDay == null) continue;
      const days = expiryDay - today;
      const critical = days >= 0 && days <= (DOC_TYPES[docType]?.criticalDays ?? 30);
      const info = {
        type: docType,
        name: d.documentName || '',
        expiryDate: dayIndexToString(expiryDay),
        daysUntilExpiry: days,
        critical,
        source: d.source || 'unknown'
      };
      status.allDocuments.push(info);
      if (days < 0) {
        status.hasExpiredDocuments = true;
        status.expiredDocuments.push(info);
      } else if (days <= EXPIRING_WINDOW_DAYS) {
        status.hasExpiringDocuments = true;
        status.expiringDocuments.push(info);
        if (critical) status.criticalDocuments.push(info);
      }
      if (status.daysUntilExpiry == null || days < status.daysUntilExpiry) {
        status.nearestExpiry = info;
        status.daysUntilExpiry = days;
      }
    }
  }
  status.allDocuments.sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);

  for (const alarm of alarms || []) {
    if (alarm?.alarmDate === todayStr) {
      status.hasTodayAlarm = true;
      status.todayAlarmDocuments.push({
        type: alarm.documentType || 'unknown',
        name: alarm.documentName || alarm.documentType || 'Unknown Document',
        alarmDate: alarm.alarmDate
      });
    }
  }
  return status;
};

const hasAnyDocuments = (user) => {
  const uploaded = Object.values(user.documents || {}).some((docs) => (docs || []).length > 0);
  const p = user.profileData || {};
  return uploaded || !!(p.passportNumber && p.passportExpiry) || !!(p.visaNumber && p.visaExpiryDate);
};

const getUserStatus = (user) => {
  const s = user.expiryStatus;
  if (s.hasExpiredDocuments) return 'expired';
  if (s.criticalDocuments.length > 0) return 'critical';
  if (s.hasExpiringDocuments) return 'expiring';
  return user.hasDocuments ? 'normal' : 'none';
};

const STATUS_META = {
  expired: { cls: 'bg-red-50 text-red-700 ring-red-200', Icon: XCircle },
  critical: { cls: 'bg-orange-50 text-orange-700 ring-orange-200', Icon: AlertTriangle },
  expiring: { cls: 'bg-amber-50 text-amber-700 ring-amber-200', Icon: Clock },
  normal: { cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200', Icon: CheckCircle },
  none: { cls: 'bg-slate-100 text-slate-600 ring-slate-200', Icon: FileText }
};

const dayTone = (days) => {
  if (days == null) return 'text-slate-400';
  if (days < 0) return 'text-red-600';
  if (days <= 14) return 'text-orange-600';
  if (days <= EXPIRING_WINDOW_DAYS) return 'text-amber-600';
  return 'text-slate-600';
};

const PERIODS = ['all', 'today_alarm', 'expired', 'critical', '7', '30', '90', 'visa_expired', 'no_documents'];

const matchesPeriod = (user, period) => {
  const s = user.expiryStatus;
  switch (period) {
    case 'all': return true;
    case 'today_alarm': return s.hasTodayAlarm;
    case 'expired': return s.hasExpiredDocuments;
    case 'critical': return s.criticalDocuments.length > 0;
    case 'visa_expired': return s.expiredDocuments.some((d) => d.type === 'visa');
    case 'no_documents': return !user.hasDocuments;
    default: {
      const days = Number(period);
      return s.expiringDocuments.some((d) => d.daysUntilExpiry <= days);
    }
  }
};

// ---------- 알림 메시지 (미얀마어) ----------
const MESSAGE_TYPES = [
  {
    key: 'warning',
    pushType: 'warning',
    title: '🚨 နိုးကြားမှု: စာရွက်စာတမ်းသက်တမ်းကုန်ခါနီးသတိပေးချက်',
    body: 'စာရွက်စာတမ်းသက်တမ်းကုန်ဆုံးရက်ကိုစစ်ဆေးပြီး သက်တမ်းတိုးခြင်းကိုပြင်ဆင်ပါ။',
    withDetails: true
  },
  {
    key: 'urgent',
    pushType: 'urgent',
    title: '⚠️ အရေးကြီးသော: စာရွက်စာတမ်းသက်တမ်းကုန်ဆုံးခါနီး',
    body: 'စာရွက်စာတမ်းသက်တမ်းတိုးခြင်းကိုချက်ချင်းလိုအပ်ပါသည်။',
    withDetails: true
  },
  {
    key: 'reminder',
    pushType: 'normal',
    title: '📅 အသိပေးချက်: စာရွက်စာတမ်းသက်တမ်းကုန်ဆုံးမည့်ရက်',
    body: 'စာရွက်စာတမ်းသက်တမ်းတိုးခြင်းကိုပြင်ဆင်ပါ။',
    withDetails: true
  },
  {
    key: 'document_registration',
    pushType: 'normal',
    title: '📋 စာရွက်စာတမ်းမှတ်ပုံတင်ခြင်းလမ်းညွှန်',
    body: "အက်ပ်ရှိ 'စာရွက်စာတမ်းစီမံခန့်ခွဲမှု' မီနူးမှ ဗီဇာ၊ ပတ်စ်ပို့၊ နိုင်ငံခြားသားမှတ်ပုံတင်ကတ်ကို မှတ်ပုံတင်ပါ။\nစာရွက်စာတမ်းမှတ်ပုံတင်ခြင်းဖြင့် သက်တမ်းကုန်ဆုံးအသိပေးချက်ဝန်ဆောင်မှုကို ရယူနိုင်ပါသည်။",
    withDetails: false
  },
  { key: 'info', pushType: 'normal', title: '', body: '', withDetails: false }
];

const mmDayText = (days) => {
  if (days < 0) return `သက်တမ်းကုန်ပြီး ${Math.abs(days)} ရက်`;
  if (days === 0) return 'ယနေ့ သက်တမ်းကုန်မည်';
  return `${days} ရက်အတွင်း သက်တမ်းကုန်မည်`;
};

const buildMessage = (typeKey, user, customText) => {
  const t = MESSAGE_TYPES.find((m) => m.key === typeKey) || MESSAGE_TYPES[0];
  if (t.key === 'info') return (customText || '').trim();
  let text = `${t.title}\n\n${t.body}`;
  if (t.withDetails && user) {
    const docs = [...user.expiryStatus.expiredDocuments, ...user.expiryStatus.expiringDocuments]
      .sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry)
      .slice(0, 5);
    if (docs.length > 0) {
      text += `\n\n${docs
        .map((d) => `• ${DOC_TYPES[d.type]?.mm || d.name || d.type}: ${d.expiryDate} (${mmDayText(d.daysUntilExpiry)})`)
        .join('\n')}`;
    }
  }
  return text;
};

const resolveRecipient = (user) => {
  const candidates = [user.accountEmail, user.email];
  const found = candidates.find((e) => typeof e === 'string' && e.includes('@'));
  return found ? found.trim().toLowerCase() : null;
};

const normalizeCachedUser = (data, id) => {
  const user = {
    ...data,
    userId: data.userId || id,
    name: data.name || data.displayName || data.fullName || 'Unknown',
    email: data.email || '',
    employer: data.employer && data.employer !== 'Unknown' ? data.employer : '',
    phone: data.phone && data.phone !== 'No phone' ? data.phone : '',
    documents: data.documents || {},
    alarms: Array.isArray(data.alarms) ? data.alarms : []
  };
  user.expiryStatus = computeExpiryStatus(user.documents, user.alarms);
  user.hasDocuments = hasAnyDocuments(user);
  user.status = getUserStatus(user);
  return user;
};

// ---------- 작은 UI 조각 ----------
const StatusBadge = ({ status }) => {
  const { t: tr } = useTranslation();
  const key = STATUS_META[status] ? status : 'none';
  const meta = STATUS_META[key];
  const { Icon } = meta;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${meta.cls}`}>
      <Icon className="h-3 w-3" />
      {tr(`docExpiry.status.${key}`)}
    </span>
  );
};

const DocChip = ({ info }) => {
  const { t: tr } = useTranslation();
  const Icon = DOC_TYPES[info.type]?.Icon || FileText;
  return (
    <Tooltip title={`${info.name || tr(`docExpiry.docType.${info.type}`, info.type)} · ${info.expiryDate} · ${tr(info.source === 'signup' ? 'docExpiry.source.signup' : 'docExpiry.source.upload')}`}>
      <span className="inline-flex items-center gap-1 rounded-md bg-slate-50 px-1.5 py-0.5 text-[11px] ring-1 ring-slate-200">
        <Icon className="h-3 w-3 text-slate-500" />
        <span className="text-slate-700">{DOC_TYPES[info.type] ? tr(`docExpiry.docType.${info.type}`) : info.type}</span>
        <span className={`font-semibold tabular-nums ${dayTone(info.daysUntilExpiry)}`}>{dDayLabel(info.daysUntilExpiry, tr)}</span>
      </span>
    </Tooltip>
  );
};

const btn = 'inline-flex !h-8 !min-h-0 items-center gap-1.5 rounded-md !px-3 !py-0 text-xs font-semibold disabled:opacity-50';

const DocumentExpiryManagementPage = () => {
  const { t: tr, i18n } = useTranslation();
  const T = (key, opts) => tr(`docExpiry.${key}`, opts);
  const lang = (i18n.language || 'ko').split('-')[0];
  const fmt = (value) => formatDateTime(value, lang);
  const docTypeName = (type) => (DOC_TYPES[type] ? T(`docType.${type}`) : type);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingCache, setUpdatingCache] = useState(false);
  const [cacheUpdatedAt, setCacheUpdatedAt] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [autoAlert, setAutoAlert] = useState(AUTO_ALERT_DEFAULTS);
  const [savingAutoAlert, setSavingAutoAlert] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState('all');
  const [docType, setDocType] = useState('all');
  const [sortBy, setSortBy] = useState('expiry');
  const [selectedIds, setSelectedIds] = useState([]);

  const [alertTargets, setAlertTargets] = useState(null);
  const [msgType, setMsgType] = useState('warning');
  const [customText, setCustomText] = useState('');
  const [alsoPush, setAlsoPush] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const [viewUser, setViewUser] = useState(null);
  const [recomputing, setRecomputing] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteValidation, setDeleteValidation] = useState(null);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const statsRef = doc(db, STATS_PATH);
      const [statsSnap, cacheSnap] = await Promise.all([
        getDoc(statsRef),
        getDocs(collection(statsRef, 'documentExpiryCache'))
      ]);
      const statsData = statsSnap.exists() ? statsSnap.data() : {};
      setCacheUpdatedAt(statsData.lastUpdated || null);
      setAutoAlert({ ...AUTO_ALERT_DEFAULTS, ...(statsData.autoAlertConfig || {}) });
      const list = cacheSnap.docs
        .map((d) => normalizeCachedUser(d.data(), d.id))
        .filter((u) => u.role !== 'admin');
      setUsers(list);
      setSelectedIds((prev) => prev.filter((id) => list.some((u) => u.userId === id)));
    } catch (error) {
      console.error('문서 만료 캐시 로드 실패:', error);
      setLoadError(error.message || String(error));
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleUpdateCache = async () => {
    setUpdatingCache(true);
    const hide = message.loading(T('cache.running'), 0);
    try {
      const run = httpsCallable(functions, 'manualDocumentExpiryUpdate', { timeout: 3600 * 1000 });
      const { data } = await run();
      message.success(
        T('cache.done', { n: data?.processed ?? 0 }) +
        (data?.failed ? T('cache.failedPart', { n: data.failed }) : '') +
        (data?.pruned ? T('cache.prunedPart', { n: data.pruned }) : '')
      );
      await loadData();
    } catch (error) {
      console.error('캐시 업데이트 실패:', error);
      const code = error?.code || '';
      const text = code.includes('permission-denied')
        ? T('cache.permissionDenied')
        : code.includes('deadline-exceeded')
          ? T('cache.deadline')
          : error.message || T('common.unknown');
      message.error(T('cache.failed', { error: text }));
    } finally {
      hide();
      setUpdatingCache(false);
    }
  };

  const saveAutoAlert = async (patch) => {
    const next = { ...autoAlert, ...patch };
    setSavingAutoAlert(true);
    try {
      await setDoc(doc(db, STATS_PATH), { autoAlertConfig: next }, { merge: true });
      setAutoAlert(next);
      message.success(next.enabled ? T('autoAlert.savedOn', { n: next.reminderIntervalDays }) : T('autoAlert.savedOff'));
    } catch (error) {
      message.error(T('autoAlert.saveFailed', { error: error.message }));
    } finally {
      setSavingAutoAlert(false);
    }
  };

  // ---------- 통계 / 필터 ----------
  const stats = useMemo(() => {
    const s = { total: users.length, expiring: 0, critical: 0, expired: 0, visaExpired: 0, noDocuments: 0, todayAlarm: 0 };
    users.forEach((u) => {
      const e = u.expiryStatus;
      if (e.hasExpiringDocuments) s.expiring++;
      if (e.criticalDocuments.length > 0) s.critical++;
      if (e.hasExpiredDocuments) s.expired++;
      if (e.expiredDocuments.some((d) => d.type === 'visa')) s.visaExpired++;
      if (!u.hasDocuments) s.noDocuments++;
      if (e.hasTodayAlarm) s.todayAlarm++;
    });
    return s;
  }, [users]);

  const STAT_CARDS = [
    { key: 'all', label: T('stat.total'), value: stats.total, Icon: Users, tone: 'text-slate-700 bg-slate-100' },
    { key: '90', label: T('stat.expiring'), value: stats.expiring, Icon: Clock, tone: 'text-amber-700 bg-amber-100' },
    { key: 'critical', label: T('stat.critical'), hint: T('stat.criticalHint'), value: stats.critical, Icon: AlertTriangle, tone: 'text-orange-700 bg-orange-100' },
    { key: 'expired', label: T('stat.expired'), value: stats.expired, Icon: XCircle, tone: 'text-red-700 bg-red-100' },
    { key: 'visa_expired', label: T('stat.visaExpired'), value: stats.visaExpired, Icon: Shield, tone: 'text-rose-700 bg-rose-100' },
    { key: 'no_documents', label: T('stat.noDocuments'), value: stats.noDocuments, Icon: FileText, tone: 'text-slate-600 bg-slate-100' },
    { key: 'today_alarm', label: T('stat.todayAlarm'), value: stats.todayAlarm, Icon: Bell, tone: 'text-violet-700 bg-violet-100' }
  ];

  const periodCounts = useMemo(() => {
    const counts = {};
    PERIODS.forEach((p) => { counts[p] = users.filter((u) => matchesPeriod(u, p)).length; });
    return counts;
  }, [users]);

  const filteredUsers = useMemo(() => {
    let list = users.filter((u) => matchesPeriod(u, period));
    if (docType !== 'all') {
      list = list.filter((u) => u.expiryStatus.allDocuments.some((d) => d.type === docType));
    }
    if (search) {
      list = list.filter((u) =>
        [u.name, u.email, u.accountEmail, u.employer, u.phone, u.userId]
          .some((v) => typeof v === 'string' && v.toLowerCase().includes(search))
      );
    }
    const sorted = [...list];
    if (sortBy === 'expiry') {
      sorted.sort((a, b) => (a.expiryStatus.daysUntilExpiry ?? Infinity) - (b.expiryStatus.daysUntilExpiry ?? Infinity));
    } else {
      const key = sortBy === 'employer' ? 'employer' : 'name';
      sorted.sort((a, b) => String(a[key] || '').localeCompare(String(b[key] || ''), 'ko'));
    }
    return sorted;
  }, [users, period, docType, search, sortBy]);

  const selectedUsers = useMemo(
    () => users.filter((u) => selectedIds.includes(u.userId)),
    [users, selectedIds]
  );

  const resetFilters = () => {
    setSearchInput('');
    setPeriod('all');
    setDocType('all');
    setSortBy('expiry');
  };

  // ---------- 알림 발송 ----------
  const openAlert = (targets) => {
    if (!targets.length) return;
    const allNoDocs = targets.every((u) => !u.hasDocuments);
    setMsgType(allNoDocs ? 'document_registration' : 'warning');
    setCustomText('');
    setSendResult(null);
    setAlertTargets(targets);
  };

  const closeAlert = () => {
    if (sending) return;
    setAlertTargets(null);
    setSendResult(null);
  };

  const sendAlerts = async () => {
    if (!alertTargets?.length) return;
    if (msgType === 'info' && !customText.trim()) {
      message.warning(T('alert.enterContent'));
      return;
    }
    setSending(true);
    const sent = [];
    const failed = [];
    const skipped = alertTargets.filter((u) => !resolveRecipient(u));
    const valid = alertTargets.filter((u) => resolveRecipient(u));

    try {
      for (let i = 0; i < valid.length; i += SEND_CONCURRENCY) {
        const chunk = valid.slice(i, i + SEND_CONCURRENCY);
        const results = await Promise.allSettled(
          chunk.map((u) => messageService.sendAdminMessage(resolveRecipient(u), u.name, buildMessage(msgType, u, customText)))
        );
        results.forEach((r, idx) => (r.status === 'fulfilled' ? sent : failed).push({ user: chunk[idx], error: r.reason?.message }));
      }

      let push = null;
      if (alsoPush && sent.length > 0) {
        const t = MESSAGE_TYPES.find((m) => m.key === msgType);
        const title = msgType === 'info' ? 'Kairos' : t.title.replace(/^\S+\s/, '');
        const body = msgType === 'info'
          ? customText.trim()
          : 'အသေးစိတ်ကို Kairos အက်ပ်ရှိ မက်ဆေ့ချ်တွင် စစ်ဆေးပါ။';
        try {
          push = await adminNotificationService.sendNotification({
            id: `doc_expiry_${Date.now()}`,
            title,
            message: body,
            type: t.pushType,
            sender: 'admin',
            targetType: 'specific_users',
            targetUsers: sent.map(({ user }) => user.userId)
          });
        } catch (error) {
          push = { success: false, error: error.message };
        }
      }

      const alertInfo = { at: serverTimestamp(), type: msgType };
      await Promise.allSettled(
        sent.map(({ user }) => updateDoc(doc(db, `${STATS_PATH}/documentExpiryCache/${user.userId}`), { lastAlert: alertInfo }))
      );
      const sentIds = new Set(sent.map(({ user }) => user.userId));
      const now = new Date();
      setUsers((prev) => prev.map((u) => (sentIds.has(u.userId) ? { ...u, lastAlert: { at: now, type: msgType } } : u)));

      setSendResult({ sent: sent.length, failed, skipped, push });
      if (sent.length > 0) setSelectedIds((prev) => prev.filter((id) => !sentIds.has(id)));
    } catch (error) {
      console.error('알림 발송 실패:', error);
      message.error(T('alert.sendFailed', { error: error.message }));
    } finally {
      setSending(false);
    }
  };

  // ---------- 상세 / 재계산 ----------
  const recomputeUser = async (user) => {
    setRecomputing(true);
    try {
      const run = httpsCallable(functions, 'updateUserDocumentExpiry');
      await run({ userId: user.userId });
      const snap = await getDoc(doc(db, `${STATS_PATH}/documentExpiryCache/${user.userId}`));
      if (snap.exists()) {
        const fresh = normalizeCachedUser(snap.data(), snap.id);
        setUsers((prev) => prev.map((u) => (u.userId === fresh.userId ? fresh : u)));
        setViewUser(fresh);
      }
      message.success(T('drawer.recomputed'));
    } catch (error) {
      message.error(T('drawer.recomputeFailed', { error: error.message }));
    } finally {
      setRecomputing(false);
    }
  };

  // ---------- 삭제 ----------
  const openDelete = async (user) => {
    setDeleteTarget(user);
    setDeleteConfirmed(false);
    setDeleteValidation(null);
    try {
      setDeleteValidation(await userDeletionService.validateUserDeletion(user.userId));
    } catch (error) {
      setDeleteValidation({ valid: false, message: error.message });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !deleteConfirmed) return;
    setDeleting(true);
    try {
      const result = await userDeletionService.deleteUserCompletely(deleteTarget.userId);
      if (!result?.success) throw new Error(result?.message || T('delete.failedShort'));
      await deleteDoc(doc(db, `${STATS_PATH}/documentExpiryCache/${deleteTarget.userId}`)).catch(() => {});
      setUsers((prev) => prev.filter((u) => u.userId !== deleteTarget.userId));
      setSelectedIds((prev) => prev.filter((id) => id !== deleteTarget.userId));
      if (viewUser?.userId === deleteTarget.userId) setViewUser(null);
      message.success(T('delete.done', { name: deleteTarget.name, n: result.details?.totalDeleted ?? 0 }));
      setDeleteTarget(null);
    } catch (error) {
      message.error(T('delete.failed', { error: error.message }));
    } finally {
      setDeleting(false);
    }
  };

  // ---------- 테이블 ----------
  const columns = [
    {
      title: T('table.user'),
      key: 'user',
      render: (_, u) => (
        <div className="min-w-0">
          <div className="truncate text-[13px] font-semibold text-slate-900">{u.name}</div>
          <div className="truncate text-[11px] text-slate-500">{u.email || '-'}</div>
          {u.employer && <div className="truncate text-[11px] text-slate-400">{u.employer}</div>}
        </div>
      )
    },
    {
      title: T('table.status'),
      key: 'status',
      width: 110,
      render: (_, u) => (
        <div className="flex flex-col items-start gap-1">
          <StatusBadge status={u.status} />
          {u.expiryStatus.hasTodayAlarm && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-600">
              <Bell className="h-3 w-3" />{T('stat.todayAlarm')}
            </span>
          )}
        </div>
      )
    },
    {
      title: T('table.docs'),
      key: 'docs',
      render: (_, u) => {
        const docs = u.expiryStatus.allDocuments;
        if (!docs.length) return <span className="text-[11px] text-slate-400">{T('table.noDocs')}</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {docs.slice(0, 3).map((d, i) => <DocChip key={`${d.type}-${i}`} info={d} />)}
            {docs.length > 3 && <span className="text-[11px] text-slate-400">+{docs.length - 3}</span>}
          </div>
        );
      }
    },
    {
      title: T('table.nearest'),
      key: 'nearest',
      width: 140,
      render: (_, u) => {
        const n = u.expiryStatus.nearestExpiry;
        if (!n) return <span className="text-[11px] text-slate-400">-</span>;
        return (
          <div>
            <div className={`text-[13px] font-bold tabular-nums ${dayTone(n.daysUntilExpiry)}`}>{dDayLabel(n.daysUntilExpiry, tr)}</div>
            <div className="text-[11px] tabular-nums text-slate-500">{n.expiryDate}</div>
          </div>
        );
      }
    },
    {
      title: T('table.lastAlert'),
      key: 'lastAlert',
      width: 130,
      render: (_, u) => (u.lastAlert?.at
        ? (
          <span className="text-[11px] tabular-nums text-slate-600">
            {fmt(u.lastAlert.at)}
            {u.lastAlert.auto && <span className="ml-1 rounded bg-rose-50 px-1 text-[10px] font-semibold text-rose-600">{T('table.auto')}</span>}
          </span>
        )
        : <span className="text-[11px] text-slate-300">{T('table.noAlert')}</span>)
    },
    {
      title: '',
      key: 'actions',
      width: 108,
      render: (_, u) => (
        <div className="flex items-center justify-end gap-0.5">
          <Tooltip title={T('actions.sendOne')}>
            <button type="button" onClick={() => openAlert([u])} className="rounded-md p-1.5 text-indigo-600 hover:bg-indigo-50">
              <Send className="h-4 w-4" />
            </button>
          </Tooltip>
          <Tooltip title={T('actions.view')}>
            <button type="button" onClick={() => setViewUser(u)} className="rounded-md p-1.5 text-slate-600 hover:bg-slate-100">
              <Eye className="h-4 w-4" />
            </button>
          </Tooltip>
          {u.expiryStatus.hasExpiredDocuments && (
            <Tooltip title={T('actions.delete')}>
              <button type="button" onClick={() => openDelete(u)} className="rounded-md p-1.5 text-red-600 hover:bg-red-50">
                <Trash2 className="h-4 w-4" />
              </button>
            </Tooltip>
          )}
        </div>
      )
    }
  ];

  const previewUser = alertTargets?.[0];
  const previewText = previewUser ? buildMessage(msgType, previewUser, customText) : '';
  const skippedCount = alertTargets ? alertTargets.filter((u) => !resolveRecipient(u)).length : 0;

  return (
    <div className="min-h-screen bg-slate-50 p-3">
      <div className="space-y-3">
        {/* Header */}
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-600">
                <Shield className="h-4 w-4 text-white" />
              </div>
              <div>
                <h1 className="text-[15px] font-semibold leading-tight text-slate-900">{T('title')}</h1>
                <p className="text-[11px] leading-tight text-slate-500">
                  {T('subtitle')} · {T('cacheUpdated', { time: fmt(cacheUpdatedAt) })} ({T('dailyAuto')}{autoAlert.enabled ? ` · ${T('autoAlert.headerNote')}` : ''})
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Tooltip title={T('autoAlert.tooltip', { n: autoAlert.expiredLookbackDays })}>
                <div className="flex !h-8 items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5">
                  <Bell className={`h-3.5 w-3.5 ${autoAlert.enabled ? 'text-rose-600' : 'text-slate-400'}`} />
                  <span className="text-xs font-semibold text-slate-700">{T('autoAlert.label')}</span>
                  <Switch size="small" checked={!!autoAlert.enabled} loading={savingAutoAlert} onChange={(v) => saveAutoAlert({ enabled: v })} />
                  <Select
                    size="small"
                    variant="borderless"
                    disabled={!autoAlert.enabled || savingAutoAlert}
                    value={Number(autoAlert.reminderIntervalDays) || 7}
                    onChange={(v) => saveAutoAlert({ reminderIntervalDays: v })}
                    className="w-[92px]"
                    options={[1, 3, 7, 14, 30].map((d) => ({ value: d, label: d === 1 ? T('autoAlert.daily') : T('autoAlert.everyN', { n: d }) }))}
                  />
                </div>
              </Tooltip>
              <Tooltip title={T('actions.refreshTip')}>
                <button type="button" onClick={loadData} disabled={loading} className={`${btn} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50`}>
                  <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                  {T('actions.refresh')}
                </button>
              </Tooltip>
              <Tooltip title={T('actions.updateCacheTip')}>
                <button type="button" onClick={handleUpdateCache} disabled={updatingCache} className={`${btn} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50`}>
                  <Zap className={`h-3.5 w-3.5 ${updatingCache ? 'animate-pulse' : ''}`} />
                  {updatingCache ? T('actions.updating') : T('actions.updateCache')}
                </button>
              </Tooltip>
              <button
                type="button"
                onClick={() => openAlert(selectedUsers)}
                disabled={selectedUsers.length === 0}
                className={`${btn} bg-slate-900 text-white hover:bg-slate-800`}
              >
                <Send className="h-3.5 w-3.5" />
                {T('actions.sendAlert', { n: selectedUsers.length })}
              </button>
            </div>
          </div>

          {/* Stat cards */}
          <div className="mt-3 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3 sm:grid-cols-4 xl:grid-cols-7">
            {STAT_CARDS.map(({ key, label, hint, value, Icon, tone }) => {
              const active = period === key;
              return (
                <Tooltip key={key} title={hint}>
                  <button
                    type="button"
                    onClick={() => setPeriod(key)}
                    className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors ${
                      active ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900' : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                  >
                    <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md ${tone}`}>
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-[10px] leading-tight text-slate-500">{label}</span>
                      <span className="block text-base font-bold leading-tight tabular-nums text-slate-900">{value.toLocaleString()}</span>
                    </span>
                  </button>
                </Tooltip>
              );
            })}
          </div>
        </div>

        {loadError && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <span>{T('loadError', { error: loadError })}</span>
          </div>
        )}

        {/* List */}
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="space-y-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-[220px] flex-1">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder={T('toolbar.search')}
                  className="h-8 w-full rounded-md border border-slate-200 bg-white pl-8 pr-7 text-xs text-slate-800 outline-none focus:border-slate-400"
                />
                {searchInput && (
                  <button type="button" onClick={() => setSearchInput('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <Select
                size="small"
                value={docType}
                onChange={setDocType}
                className="w-36"
                options={[{ value: 'all', label: T('toolbar.allDocTypes') }, ...Object.keys(DOC_TYPES).map((k) => ({ value: k, label: docTypeName(k) }))]}
              />
              <Select
                size="small"
                value={sortBy}
                onChange={setSortBy}
                className="w-32"
                options={[
                  { value: 'expiry', label: T('toolbar.sortExpiry') },
                  { value: 'name', label: T('toolbar.sortName') },
                  { value: 'employer', label: T('toolbar.sortEmployer') }
                ]}
              />
              <button type="button" onClick={resetFilters} className={`${btn} border border-slate-200 bg-white text-slate-600 hover:bg-slate-100`}>
                {T('toolbar.reset')}
              </button>
            </div>
            <div className="flex flex-wrap gap-1">
              {PERIODS.map((p) => {
                const active = period === p;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPeriod(p)}
                    className={`inline-flex !h-7 !min-h-0 items-center gap-1 rounded-full !px-2.5 !py-0 text-[11px] font-semibold transition-colors ${
                      active ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    {T(`period.${p}`)}
                    <span className={`tabular-nums ${active ? 'text-white/70' : 'text-slate-400'}`}>{periodCounts[p] ?? 0}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {selectedIds.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-indigo-100 bg-indigo-50/60 px-3 py-1.5 text-xs">
              <span className="font-semibold text-indigo-800">{T('selection.count', { n: selectedIds.length })}</span>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => openAlert(selectedUsers)} className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700`}>
                  <Send className="h-3.5 w-3.5" />
                  {T('selection.send')}
                </button>
                <button type="button" onClick={() => setSelectedIds([])} className="font-semibold text-indigo-700 hover:underline">
                  {T('selection.clear')}
                </button>
              </div>
            </div>
          )}

          <Table
            size="small"
            rowKey="userId"
            loading={loading}
            columns={columns}
            dataSource={filteredUsers}
            rowSelection={{
              selectedRowKeys: selectedIds,
              preserveSelectedRowKeys: true,
              onChange: (keys) => setSelectedIds(keys),
              selections: [
                Table.SELECTION_INVERT,
                {
                  key: 'all-filtered',
                  text: T('selection.allFiltered', { n: filteredUsers.length }),
                  onSelect: () => setSelectedIds((prev) => Array.from(new Set([...prev, ...filteredUsers.map((u) => u.userId)])))
                },
                Table.SELECTION_NONE
              ]
            }}
            onRow={(u) => ({ onDoubleClick: () => setViewUser(u) })}
            pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: [20, 50, 100], showTotal: (n) => T('table.total', { n: n.toLocaleString() }), size: 'small' }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={users.length === 0 ? T('table.emptyNoCache') : T('table.emptyNoMatch')}
                />
              )
            }}
            scroll={{ x: 900 }}
          />
        </div>
      </div>

      {/* Send Alert Modal */}
      <Modal
        open={!!alertTargets}
        onCancel={closeAlert}
        maskClosable={!sending}
        width={620}
        title={
          <div className="flex items-center gap-2">
            <Send className="h-4 w-4 text-indigo-600" />
            <span className="text-sm font-semibold">
              {alertTargets?.length === 1 ? T('alert.titleOne', { name: alertTargets[0].name }) : T('alert.titleMany', { n: alertTargets?.length || 0 })}
            </span>
          </div>
        }
        footer={
          sendResult ? (
            <button type="button" onClick={closeAlert} className={`${btn} bg-slate-900 text-white hover:bg-slate-800`}>{T('common.close')}</button>
          ) : (
            <div className="flex justify-end gap-2">
              <button type="button" onClick={closeAlert} disabled={sending} className={`${btn} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50`}>{T('common.cancel')}</button>
              <button
                type="button"
                onClick={sendAlerts}
                disabled={sending || (msgType === 'info' && !customText.trim()) || (alertTargets?.length || 0) === skippedCount}
                className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700`}
              >
                {sending ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {sending ? T('alert.sending') : T('alert.sendTo', { n: ((alertTargets?.length || 0) - skippedCount).toLocaleString() })}
              </button>
            </div>
          )
        }
      >
        {sendResult ? (
          <div className="space-y-2 text-xs">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                <div className="text-[10px] text-emerald-700">{T('alert.resultSent')}</div>
                <div className="text-lg font-bold tabular-nums text-emerald-800">{sendResult.sent}</div>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                <div className="text-[10px] text-red-700">{T('alert.resultFailed')}</div>
                <div className="text-lg font-bold tabular-nums text-red-800">{sendResult.failed.length}</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="text-[10px] text-slate-600">{T('alert.resultSkipped')}</div>
                <div className="text-lg font-bold tabular-nums text-slate-800">{sendResult.skipped.length}</div>
              </div>
            </div>
            {sendResult.push && (
              <div className={`rounded-lg border px-3 py-2 ${sendResult.push.success ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                {sendResult.push.success
                  ? T('alert.pushOk', { n: sendResult.push.sentCount ?? 0 }) + (sendResult.push.failureCount ? T('alert.pushFailPart', { n: sendResult.push.failureCount }) : '')
                  : sendResult.push.reason === 'no_tokens'
                    ? T('alert.pushNoTokens')
                    : T('alert.pushFailed', { error: sendResult.push.error || sendResult.push.reason || sendResult.push.message || T('common.unknown') })}
              </div>
            )}
            {(sendResult.failed.length > 0 || sendResult.skipped.length > 0) && (
              <div className="max-h-40 overflow-auto rounded-lg border border-slate-200 p-2">
                {sendResult.failed.map(({ user, error }) => (
                  <div key={user.userId} className="text-red-700">• {user.name} ({user.email}) — {error || T('alert.resultFailed')}</div>
                ))}
                {sendResult.skipped.map((u) => (
                  <div key={u.userId} className="text-slate-600">• {u.name} — {T('alert.noEmail')}</div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
              {MESSAGE_TYPES.map((m) => {
                const active = msgType === m.key;
                return (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setMsgType(m.key)}
                    className={`rounded-lg border px-2.5 py-2 text-left transition-colors ${
                      active ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500' : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                  >
                    <div className={`text-xs font-semibold ${active ? 'text-indigo-800' : 'text-slate-800'}`}>{T(`msgType.${m.key}.label`)}</div>
                    <div className="text-[10px] text-slate-500">{T(`msgType.${m.key}.desc`)}</div>
                  </button>
                );
              })}
            </div>

            {msgType === 'info' && (
              <textarea
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                rows={5}
                placeholder={T('alert.customPlaceholder')}
                className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-xs outline-none focus:border-indigo-400"
              />
            )}

            <div>
              <div className="mb-1 flex items-center justify-between text-[11px] text-slate-500">
                <span className="inline-flex items-center gap-1 font-semibold text-slate-700">
                  <MessageSquare className="h-3.5 w-3.5" />
                  {T('alert.preview')}{previewUser && alertTargets.length > 1 ? T('alert.previewBasis', { name: previewUser.name }) : ''}
                </span>
                <span>{MESSAGE_TYPES.find((m) => m.key === msgType)?.withDetails ? T('alert.autoDetails') : ''}</span>
              </div>
              <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-sans text-xs leading-relaxed text-slate-800">
                {previewText || T('alert.empty')}
              </pre>
            </div>

            <Checkbox checked={alsoPush} onChange={(e) => setAlsoPush(e.target.checked)}>
              <span className="text-xs text-slate-700">{T('alert.alsoPush')}</span>
            </Checkbox>

            {skippedCount > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                {T('alert.skippedWarn', { n: skippedCount })}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Detail Drawer */}
      <Drawer
        open={!!viewUser}
        onClose={() => setViewUser(null)}
        width={460}
        title={viewUser ? <span className="text-sm font-semibold">{viewUser.name}</span> : null}
        extra={viewUser && <StatusBadge status={viewUser.status} />}
      >
        {viewUser && (
          <div className="space-y-4 text-xs">
            <div className="grid grid-cols-[88px_1fr] gap-y-1.5 rounded-lg border border-slate-200 p-3">
              <span className="text-slate-500">{T('drawer.email')}</span><span className="break-all text-slate-800">{viewUser.email || '-'}</span>
              {viewUser.accountEmail && viewUser.accountEmail !== viewUser.email?.toLowerCase() && (
                <><span className="text-slate-500">{T('drawer.loginAccount')}</span><span className="break-all text-slate-800">{viewUser.accountEmail}</span></>
              )}
              <span className="text-slate-500">{T('drawer.phone')}</span><span className="text-slate-800">{viewUser.phone || '-'}</span>
              <span className="text-slate-500">{T('drawer.employer')}</span><span className="text-slate-800">{viewUser.employer || '-'}</span>
              <span className="text-slate-500">UID</span><span className="break-all font-mono text-[11px] text-slate-600">{viewUser.userId}</span>
              <span className="text-slate-500">{T('drawer.cacheUpdated')}</span><span className="text-slate-800">{fmt(viewUser.lastUpdated)}</span>
              <span className="text-slate-500">{T('table.lastAlert')}</span><span className="text-slate-800">{fmt(viewUser.lastAlert?.at)}</span>
            </div>

            <div>
              <div className="mb-1.5 font-semibold text-slate-800">{T('drawer.documents', { n: viewUser.expiryStatus.allDocuments.length })}</div>
              {viewUser.expiryStatus.allDocuments.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 p-3 text-center text-slate-400">{T('drawer.noDocuments')}</div>
              ) : (
                <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {viewUser.expiryStatus.allDocuments.map((d, i) => {
                    const Icon = DOC_TYPES[d.type]?.Icon || FileText;
                    return (
                      <div key={`${d.type}-${i}`} className="flex items-center justify-between gap-2 px-3 py-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <Icon className="h-4 w-4 flex-shrink-0 text-slate-500" />
                          <div className="min-w-0">
                            <div className="truncate font-medium text-slate-800">{d.name || docTypeName(d.type)}</div>
                            <div className="text-[11px] text-slate-500">{d.expiryDate} · {T(d.source === 'signup' ? 'source.signup' : 'source.upload')}</div>
                          </div>
                        </div>
                        <span className={`flex-shrink-0 font-bold tabular-nums ${dayTone(d.daysUntilExpiry)}`}>{dDayLabel(d.daysUntilExpiry, tr)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {viewUser.alarms.length > 0 && (
              <div>
                <div className="mb-1.5 font-semibold text-slate-800">{T('drawer.alarms', { n: viewUser.alarms.length })}</div>
                <div className="flex flex-wrap gap-1">
                  {viewUser.alarms.map((a, i) => (
                    <span
                      key={a.id || i}
                      className={`rounded-md px-1.5 py-0.5 text-[11px] ring-1 ${a.alarmDate === kstTodayString() ? 'bg-violet-50 text-violet-700 ring-violet-200' : 'bg-slate-50 text-slate-600 ring-slate-200'}`}
                    >
                      {a.documentName || (a.documentType ? docTypeName(a.documentType) : T('drawer.docFallback'))} · {a.alarmDate || '-'}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
              <button type="button" onClick={() => openAlert([viewUser])} className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700`}>
                <Send className="h-3.5 w-3.5" />{T('actions.sendOne')}
              </button>
              <button type="button" onClick={() => recomputeUser(viewUser)} disabled={recomputing} className={`${btn} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50`}>
                <RefreshCw className={`h-3.5 w-3.5 ${recomputing ? 'animate-spin' : ''}`} />{T('drawer.recompute')}
              </button>
              {viewUser.expiryStatus.hasExpiredDocuments && (
                <button type="button" onClick={() => openDelete(viewUser)} className={`${btn} border border-red-200 bg-white text-red-600 hover:bg-red-50`}>
                  <Trash2 className="h-3.5 w-3.5" />{T('drawer.delete')}
                </button>
              )}
            </div>
          </div>
        )}
      </Drawer>

      {/* Delete Modal */}
      <Modal
        open={!!deleteTarget}
        onCancel={() => !deleting && setDeleteTarget(null)}
        width={480}
        title={<span className="text-sm font-semibold text-red-700">{T('actions.delete')}</span>}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setDeleteTarget(null)} disabled={deleting} className={`${btn} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50`}>{T('common.cancel')}</button>
            <button
              type="button"
              onClick={confirmDelete}
              disabled={deleting || !deleteConfirmed || !deleteValidation?.valid}
              className={`${btn} bg-red-600 text-white hover:bg-red-700`}
            >
              {deleting ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              {deleting ? T('delete.deleting') : T('delete.confirm')}
            </button>
          </div>
        }
      >
        {deleteTarget && (
          <div className="space-y-3 text-xs">
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="font-semibold text-slate-900">{deleteTarget.name}</div>
              <div className="text-slate-500">{deleteTarget.email}</div>
              <div className="mt-2 space-y-0.5">
                {deleteTarget.expiryStatus.expiredDocuments.map((d, i) => (
                  <div key={i} className="text-red-700">• {d.name || docTypeName(d.type)}: {d.expiryDate} ({dDayLabel(d.daysUntilExpiry, tr)})</div>
                ))}
              </div>
            </div>
            {!deleteValidation ? (
              <div className="flex items-center gap-1.5 text-slate-500"><RefreshCw className="h-3.5 w-3.5 animate-spin" />{T('delete.checking')}</div>
            ) : !deleteValidation.valid ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700">{deleteValidation.message}</div>
            ) : !deleteValidation.hasExpiredVisa ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
                {T('delete.visaValid')}
              </div>
            ) : (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700">{T('delete.visaExpired')}</div>
            )}
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700">
              {T('delete.warning')}
            </div>
            <Checkbox checked={deleteConfirmed} onChange={(e) => setDeleteConfirmed(e.target.checked)}>
              <span className="text-xs text-slate-700">{T('delete.confirmText', { name: deleteTarget.name })}</span>
            </Checkbox>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DocumentExpiryManagementPage;
